// コネクタ（接続矢印）の線の形・線のパターン・両端の形状・手動経路の型、既定値、上限、検証、
// patch の適用を、サーバ（WebSocket の op 適用・REST / MCP）とクライアント（楽観適用・選択パネル・
// 描画）で共有する。経路そのものの幾何計算は connector-geometry.ts が担う。
//
// 保存フィールドは後方互換のためすべて任意で、未指定は既定値として読む（旧データの一括移行はしない）。

import { CANVAS_SIZE_PRESETS, normalizeCanvasSizePreset } from "./canvas-bounds";
import type { ConnectorCanvasObject } from "./canvas-object";

/** 線の形。elbowed は角を丸めた直交折れ線、straight は端点どうしの 1 本の線分、curved は曲線。 */
export const CONNECTOR_LINE_TYPES = ["elbowed", "straight", "curved"] as const;
export type ConnectorLineType = (typeof CONNECTOR_LINE_TYPES)[number];

/**
 * 線のパターン（実線・破線）。図形・ペンの strokePattern（solid / dotted）とは別の項目で、
 * コネクタの破線は線幅を基準にした間隔で描く。
 */
export const CONNECTOR_LINE_PATTERNS = ["solid", "dashed"] as const;
export type ConnectorLinePattern = (typeof CONNECTOR_LINE_PATTERNS)[number];

/**
 * 端の形状。FigJam の公式 ConnectorStrokeCap の 12 種に対応する
 * （NONE / ARROW_LINES / ARROW_EQUILATERAL / TRIANGLE_FILLED / DIAMOND_FILLED / CIRCLE_FILLED /
 * ERD_ZERO_OR_ONE / ERD_EXACTLY_ONE / ERD_ZERO_OR_MORE / ERD_ONE_OR_MORE / ERD_ONE / ERD_MANY）。
 * 並び順は選択パネルの表示順を兼ねる。
 */
export const CONNECTOR_CAPS = [
  "none",
  "arrow-lines",
  "arrow-equilateral",
  "triangle-filled",
  "diamond-filled",
  "circle-filled",
  "erd-zero-or-one",
  "erd-exactly-one",
  "erd-zero-or-more",
  "erd-one-or-more",
  "erd-one",
  "erd-many",
] as const;
export type ConnectorCap = (typeof CONNECTOR_CAPS)[number];

/** 端の形状の利用者向けの名前（選択パネル・読み上げで使う）。 */
export const CONNECTOR_CAP_LABELS: Readonly<Record<ConnectorCap, string>> = {
  none: "なし",
  "arrow-lines": "開いた矢印",
  "arrow-equilateral": "塗り三角矢印",
  "triangle-filled": "逆向き塗り三角",
  "diamond-filled": "塗りひし形",
  "circle-filled": "塗り丸",
  "erd-zero-or-one": "0 または 1",
  "erd-exactly-one": "必ず 1",
  "erd-zero-or-more": "0 以上",
  "erd-one-or-more": "1 以上",
  "erd-one": "1",
  "erd-many": "多",
};

/** 線の形の利用者向けの名前。 */
export const CONNECTOR_LINE_TYPE_LABELS: Readonly<Record<ConnectorLineType, string>> = {
  elbowed: "角丸の折れ線",
  straight: "直線",
  curved: "曲線",
};

/** 線のパターンの利用者向けの名前。 */
export const CONNECTOR_LINE_PATTERN_LABELS: Readonly<Record<ConnectorLinePattern, string>> = {
  solid: "実線",
  dashed: "破線",
};

/** 未指定の線の形。従来の見た目（角丸の直交折れ線）。 */
export const CONNECTOR_DEFAULT_LINE_TYPE: ConnectorLineType = "elbowed";
/** 未指定の線のパターン。 */
export const CONNECTOR_DEFAULT_LINE_PATTERN: ConnectorLinePattern = "solid";
/** 未指定の始点の形状。従来どおり何も描かない。 */
export const CONNECTOR_DEFAULT_START_CAP: ConnectorCap = "none";
/** 未指定の終点の形状。従来どおり開いた矢印を描く。 */
export const CONNECTOR_DEFAULT_END_CAP: ConnectorCap = "arrow-lines";

/** 手動経路に保存できるチャネル（通過する縦線・横線）の最大件数。 */
export const CONNECTOR_ROUTE_CONSTRAINTS_MAX = 16;

/**
 * 手動経路のチャネルの向き。`x` は縦のチャネル（その x 座標を縦に通る）、`y` は横のチャネル。
 */
export type ConnectorChannelAxis = "x" | "y";

/**
 * 手動経路のチャネル 1 本。`offset` は始点の接続アンカー（from 側の辺の中点）からの相対値（canvas px）で、
 * チャネルの位置は「始点アンカーの x（または y）+ offset」になる。相対値にしておくことで、両端の図形を
 * 一緒に動かしても手動の経路の形を保てる。`id` はハンドルの識別に使う一意な文字列（英数字とハイフンの
 * 1〜64 文字。Web は 16 文字の乱数の id を採番する）。
 */
export interface ConnectorRouteConstraint {
  readonly id: string;
  readonly axis: ConnectorChannelAxis;
  readonly offset: number;
}

/**
 * 経路の指定。`auto` は自動経路、`manual` は利用者が区間をドラッグして決めたチャネルを順に通る。
 * 手動経路は折れ線と曲線で共有し、直線では使わない（直線は常に auto）。
 */
export type ConnectorRoute =
  | { readonly mode: "auto" }
  | { readonly mode: "manual"; readonly constraints: readonly ConnectorRouteConstraint[] };

/** 自動経路。 */
export const CONNECTOR_AUTO_ROUTE: ConnectorRoute = { mode: "auto" };

/** 既定値を解決した、コネクタの見た目と経路。 */
export interface ResolvedConnectorAppearance {
  readonly lineType: ConnectorLineType;
  readonly linePattern: ConnectorLinePattern;
  readonly startCap: ConnectorCap;
  readonly endCap: ConnectorCap;
  readonly route: ConnectorRoute;
}

/** 見た目と経路の項目（patch のキー）。並び順は op の正規形を作るときの固定順を兼ねる。 */
export const CONNECTOR_PATCH_KEYS = [
  "lineType",
  "linePattern",
  "startCap",
  "endCap",
  "route",
] as const;
export type ConnectorPatchKey = (typeof CONNECTOR_PATCH_KEYS)[number];

/**
 * 見た目と経路の部分更新。指定した項目だけを置き換える。線の色・太さはここへ含めず、
 * 従来どおり `object.style.update` で扱う（更新前のクライアントも理解できる op のまま保つため）。
 */
export type ConnectorPatch = Partial<ResolvedConnectorAppearance>;

/** 保存値を持たない場合も含め、コネクタの見た目と経路を既定値で解決する。 */
export function resolveConnectorAppearance(
  object: Pick<ConnectorCanvasObject, ConnectorPatchKey>
): ResolvedConnectorAppearance {
  return {
    lineType: isConnectorLineType(object.lineType) ? object.lineType : CONNECTOR_DEFAULT_LINE_TYPE,
    linePattern: isConnectorLinePattern(object.linePattern)
      ? object.linePattern
      : CONNECTOR_DEFAULT_LINE_PATTERN,
    startCap: isConnectorCap(object.startCap) ? object.startCap : CONNECTOR_DEFAULT_START_CAP,
    endCap: isConnectorCap(object.endCap) ? object.endCap : CONNECTOR_DEFAULT_END_CAP,
    route: isConnectorRouteShape(object.route) ? object.route : CONNECTOR_AUTO_ROUTE,
  };
}

export function isConnectorLineType(value: unknown): value is ConnectorLineType {
  return typeof value === "string" && (CONNECTOR_LINE_TYPES as readonly string[]).includes(value);
}

export function isConnectorLinePattern(value: unknown): value is ConnectorLinePattern {
  return (
    typeof value === "string" && (CONNECTOR_LINE_PATTERNS as readonly string[]).includes(value)
  );
}

export function isConnectorCap(value: unknown): value is ConnectorCap {
  return typeof value === "string" && (CONNECTOR_CAPS as readonly string[]).includes(value);
}

/** チャネルの id として受け付ける形（UUID などの英数字とハイフン、1〜64 文字）。 */
const CONSTRAINT_ID_PATTERN = /^[A-Za-z0-9-]{1,64}$/;

/**
 * 保存値の経路として描いてよいかを確かめる（件数・id・軸・有限の数値・軸の交互と、最大のプリセットの
 * 大きさによる offset の上限）。上限を超える保存値（API を通らない不正なデータ）は既定値（自動経路）で
 * 描く。キャンバスごとの上限は、経路を指定する更新のときに {@link validateConnectorRoute} で確かめる
 * （縮めたキャンバスで上限を超えた保存済みの経路は、ここでは保つ）。
 */
function isConnectorRouteShape(value: unknown): value is ConnectorRoute {
  return validateConnectorRoute(value, CONNECTOR_ROUTE_STATIC_OFFSET_LIMITS).ok;
}

export type ConnectorValidationResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: string };

/**
 * offset の絶対値の上限（canvas px）。値の健全性のための上限で、チャネルは始点アンカーからの相対値のため、
 * 始点がキャンバスの内側にあれば、キャンバスの対応する軸の長さを超える offset はキャンバスの外側を指す
 * （始点をキャンバスの外に置いた接続矢印では、キャンバスの中でも始点から遠い位置へは置けない）。
 */
export interface ConnectorRouteOffsetLimits {
  readonly maxAbsX: number;
  readonly maxAbsY: number;
}

/**
 * offset の静的な上限。最大のキャンバスプリセットの各軸の長さで、これを超える位置はどのキャンバスでも
 * キャンバスの外を指す。キャンバスが分からない入力（API の形の検証・クリップボードの貼り付けデータ）に使い、
 * キャンバスごとの上限は {@link connectorRouteOffsetLimitsFor} で求める。
 */
export const CONNECTOR_ROUTE_STATIC_OFFSET_LIMITS: ConnectorRouteOffsetLimits = {
  maxAbsX: Math.max(...Object.values(CANVAS_SIZE_PRESETS).map((preset) => preset.width)),
  maxAbsY: Math.max(...Object.values(CANVAS_SIZE_PRESETS).map((preset) => preset.height)),
};

/**
 * キャンバスの大きさ（プリセット）に応じた offset の上限。未知・未指定のプリセットは既定（中）として扱う。
 */
export function connectorRouteOffsetLimitsFor(
  sizePreset: string | null | undefined
): ConnectorRouteOffsetLimits {
  const size = CANVAS_SIZE_PRESETS[normalizeCanvasSizePreset(sizePreset)];
  return { maxAbsX: size.width, maxAbsY: size.height };
}

/**
 * 経路の指定を検証する。手動経路は、件数（{@link CONNECTOR_ROUTE_CONSTRAINTS_MAX} 以下）・id の形と
 * 重複・軸・有限の offset・同じ軸が連続しない正規形を満たす必要がある。`limits` を渡した場合は
 * offset の絶対値の上限も確かめる。
 *
 * 不正な値の内容（offset の値や id）は理由へ含めない（ログ・応答へ利用者の入力を反射しないため）。
 */
export function validateConnectorRoute(
  value: unknown,
  limits?: ConnectorRouteOffsetLimits | undefined
): ConnectorValidationResult {
  if (typeof value !== "object" || value === null) {
    return { ok: false, reason: "経路の指定の形が正しくありません。" };
  }
  const record = value as Record<string, unknown>;
  if (record.mode === "auto") {
    return Object.keys(record).length === 1
      ? { ok: true }
      : { ok: false, reason: "自動経路には追加の項目を指定できません。" };
  }
  if (record.mode !== "manual") {
    return { ok: false, reason: "経路の種類が正しくありません。" };
  }
  if (Object.keys(record).some((key) => key !== "mode" && key !== "constraints")) {
    return { ok: false, reason: "手動経路の項目が正しくありません。" };
  }
  const constraints = record.constraints;
  if (!Array.isArray(constraints)) {
    return { ok: false, reason: "手動経路のチャネルが配列ではありません。" };
  }
  if (constraints.length > CONNECTOR_ROUTE_CONSTRAINTS_MAX) {
    return {
      ok: false,
      reason: `手動経路のチャネルは ${CONNECTOR_ROUTE_CONSTRAINTS_MAX} 件までです。`,
    };
  }
  // チャネルの無い手動経路は自動経路と同じ形にしかならず、補正中の表示が出続けるため受け付けない。
  if (constraints.length === 0) {
    return {
      ok: false,
      reason: "手動経路にはチャネルを 1 件以上指定してください（自動経路は mode: auto で指定します）。",
    };
  }
  const ids = new Set<string>();
  let previousAxis: ConnectorChannelAxis | null = null;
  for (const constraint of constraints as unknown[]) {
    if (typeof constraint !== "object" || constraint === null) {
      return { ok: false, reason: "手動経路のチャネルの形が正しくありません。" };
    }
    const item = constraint as Record<string, unknown>;
    if (Object.keys(item).some((key) => key !== "id" && key !== "axis" && key !== "offset")) {
      return { ok: false, reason: "手動経路のチャネルの項目が正しくありません。" };
    }
    if (typeof item.id !== "string" || !CONSTRAINT_ID_PATTERN.test(item.id)) {
      return { ok: false, reason: "手動経路のチャネルの id が正しくありません。" };
    }
    if (ids.has(item.id)) {
      return { ok: false, reason: "手動経路のチャネルの id が重複しています。" };
    }
    ids.add(item.id);
    if (item.axis !== "x" && item.axis !== "y") {
      return { ok: false, reason: "手動経路のチャネルの向きが正しくありません。" };
    }
    if (typeof item.offset !== "number" || !Number.isFinite(item.offset)) {
      return { ok: false, reason: "手動経路のチャネルの位置が有限の数値ではありません。" };
    }
    if (limits) {
      const max = item.axis === "x" ? limits.maxAbsX : limits.maxAbsY;
      if (Math.abs(item.offset) > max) {
        return { ok: false, reason: "手動経路のチャネルの位置がキャンバスの範囲を超えています。" };
      }
    }
    if (previousAxis === item.axis) {
      return { ok: false, reason: "手動経路のチャネルは縦と横を交互に並べてください。" };
    }
    previousAxis = item.axis;
  }
  return { ok: true };
}

/**
 * 見た目と経路の組み合わせの不変条件を確かめる。直線は端点どうしを結ぶ 1 本の線分のため、手動経路を
 * 持てない（直線へ切り替える操作は、同じ更新で経路を自動へ戻す）。
 */
export function validateConnectorAppearance(
  appearance: Pick<ConnectorCanvasObject, ConnectorPatchKey>,
  limits?: ConnectorRouteOffsetLimits | undefined
): ConnectorValidationResult {
  if (appearance.lineType !== undefined && !isConnectorLineType(appearance.lineType)) {
    return { ok: false, reason: "線の形が正しくありません。" };
  }
  if (appearance.linePattern !== undefined && !isConnectorLinePattern(appearance.linePattern)) {
    return { ok: false, reason: "線の種類が正しくありません。" };
  }
  if (appearance.startCap !== undefined && !isConnectorCap(appearance.startCap)) {
    return { ok: false, reason: "始点の形状が正しくありません。" };
  }
  if (appearance.endCap !== undefined && !isConnectorCap(appearance.endCap)) {
    return { ok: false, reason: "終点の形状が正しくありません。" };
  }
  if (appearance.route !== undefined) {
    const route = validateConnectorRoute(appearance.route, limits);
    if (!route.ok) return route;
  }
  if (appearance.lineType === "straight" && appearance.route?.mode === "manual") {
    return { ok: false, reason: "直線には手動の経路を指定できません。" };
  }
  return { ok: true };
}

/**
 * 現在の値に patch を適用した後の見た目と経路を検証する（更新の検証の共通入口）。
 *
 * - patch に含まない項目は既定値まで解決した値で確かめる（不正・旧形式の保存値で、関係の無い項目の
 *   編集まで止めない。保存値そのものは書き換えない）。
 * - キャンバスの大きさによる位置の上限（`limits`）は、patch が経路を指定するときだけ確かめる
 *   （キャンバスを縮めた後も、変えていない保存済みの経路を理由に他の項目の編集を拒否しない）。
 * - 直線と手動経路の組み合わせは、適用後の全体で確かめる。
 */
export function validateConnectorPatchResult(
  current: Pick<ConnectorCanvasObject, ConnectorPatchKey>,
  patch: ConnectorPatch,
  limits?: ConnectorRouteOffsetLimits | undefined
): ConnectorValidationResult {
  return validateConnectorAppearance(
    { ...resolveConnectorAppearance(current), ...definedConnectorPatch(patch) },
    patch.route !== undefined ? limits : undefined
  );
}

/** patch から値が undefined の項目を除く（解決済みの値を undefined で上書きしないため）。 */
function definedConnectorPatch(patch: ConnectorPatch): ConnectorPatch {
  const defined: { -readonly [K in ConnectorPatchKey]?: ConnectorPatch[K] | undefined } = {};
  for (const key of connectorPatchKeys(patch)) {
    if (key === "route") defined.route = patch.route;
    else if (key === "lineType") defined.lineType = patch.lineType;
    else if (key === "linePattern") defined.linePattern = patch.linePattern;
    else if (key === "startCap") defined.startCap = patch.startCap;
    else defined.endCap = patch.endCap;
  }
  return defined;
}

/** patch が 1 項目以上を持ち、未知のキーを含まないか。 */
export function isConnectorPatchShape(patch: unknown): patch is ConnectorPatch {
  if (typeof patch !== "object" || patch === null) return false;
  const keys = Object.keys(patch);
  return (
    keys.length > 0 &&
    keys.every((key) => (CONNECTOR_PATCH_KEYS as readonly string[]).includes(key))
  );
}

/**
 * patch をコネクタへ適用した次の状態を返す（検証はしない。呼び出し側が
 * {@link validateConnectorAppearance} で確かめる）。指定の無い項目は現状のまま残す。
 */
export function applyConnectorPatch(
  current: ConnectorCanvasObject,
  patch: ConnectorPatch
): ConnectorCanvasObject {
  const next: { -readonly [K in keyof ConnectorCanvasObject]: ConnectorCanvasObject[K] } = {
    ...current,
  };
  if (patch.lineType !== undefined) next.lineType = patch.lineType;
  if (patch.linePattern !== undefined) next.linePattern = patch.linePattern;
  if (patch.startCap !== undefined) next.startCap = patch.startCap;
  if (patch.endCap !== undefined) next.endCap = patch.endCap;
  if (patch.route !== undefined) next.route = cloneConnectorRoute(patch.route);
  return next;
}

/** patch のうち、現在の値（既定値を解決済み）と異なる項目があるか。無ければ更新は不要。 */
export function connectorPatchChangesObject(
  current: Pick<ConnectorCanvasObject, ConnectorPatchKey>,
  patch: ConnectorPatch
): boolean {
  const resolved = resolveConnectorAppearance(current);
  return CONNECTOR_PATCH_KEYS.some(
    (key) => patch[key] !== undefined && !isSameConnectorPatchValue(key, resolved[key], patch[key])
  );
}

/**
 * `expected`（変更前の値として送られた値）が現在の値（既定値を解決済み）と一致するかを確かめる。
 * サーバは一致しない更新を競合として拒否し、他の利用者が先に変えた項目を古い操作で上書きしない。
 */
export function connectorMatchesExpected(
  current: Pick<ConnectorCanvasObject, ConnectorPatchKey>,
  expected: ConnectorPatch
): boolean {
  const resolved = resolveConnectorAppearance(current);
  return CONNECTOR_PATCH_KEYS.every(
    (key) =>
      expected[key] === undefined || isSameConnectorPatchValue(key, resolved[key], expected[key])
  );
}

/** 現在の値（既定値を解決済み）から、patch と同じキーの値を取り出す（逆操作・競合照合に使う）。 */
export function pickConnectorAppearance(
  current: Pick<ConnectorCanvasObject, ConnectorPatchKey>,
  keys: readonly ConnectorPatchKey[]
): ConnectorPatch {
  const resolved = resolveConnectorAppearance(current);
  const picked: {
    -readonly [K in ConnectorPatchKey]?: ResolvedConnectorAppearance[K] | undefined;
  } = {};
  for (const key of keys) {
    if (key === "route") picked.route = cloneConnectorRoute(resolved.route);
    else if (key === "lineType") picked.lineType = resolved.lineType;
    else if (key === "linePattern") picked.linePattern = resolved.linePattern;
    else if (key === "startCap") picked.startCap = resolved.startCap;
    else picked.endCap = resolved.endCap;
  }
  return picked;
}

/** patch に含まれる項目のキー（固定順）。 */
export function connectorPatchKeys(patch: ConnectorPatch): ConnectorPatchKey[] {
  return CONNECTOR_PATCH_KEYS.filter((key) => patch[key] !== undefined);
}

function isSameConnectorPatchValue(key: ConnectorPatchKey, left: unknown, right: unknown): boolean {
  if (key !== "route") return left === right;
  return isSameConnectorRoute(left as ConnectorRoute, right as ConnectorRoute);
}

/** 2 つの経路の指定が同じか（手動経路はチャネルの順序・id・軸・位置がすべて一致するときだけ同じ）。 */
export function isSameConnectorRoute(left: ConnectorRoute, right: ConnectorRoute): boolean {
  if (left.mode !== right.mode) return false;
  if (left.mode === "auto" || right.mode === "auto") return true;
  if (left.constraints.length !== right.constraints.length) return false;
  return left.constraints.every((constraint, index) => {
    const other = right.constraints[index];
    return (
      other !== undefined &&
      other.id === constraint.id &&
      other.axis === constraint.axis &&
      other.offset === constraint.offset
    );
  });
}

/** 経路の指定を複製する（保存値と op の値が同じ配列を共有して、片方の変更が他方へ波及しないように）。 */
export function cloneConnectorRoute(route: ConnectorRoute): ConnectorRoute {
  if (route.mode === "auto") return CONNECTOR_AUTO_ROUTE;
  return {
    mode: "manual",
    constraints: route.constraints.map((constraint) => ({
      id: constraint.id,
      axis: constraint.axis,
      offset: constraint.offset,
    })),
  };
}

/**
 * 2 つのコネクタの保存内容（位置・大きさ・回転・重なり順・ロック・端点・色・太さ・見た目・経路）が
 * 同じか。変化の無い更新を保存せず、競合検知の revision も進めないために使う。
 */
export function isSameConnectorState(
  left: ConnectorCanvasObject,
  right: ConnectorCanvasObject
): boolean {
  const leftAppearance = resolveConnectorAppearance(left);
  const rightAppearance = resolveConnectorAppearance(right);
  return (
    left.x === right.x &&
    left.y === right.y &&
    left.w === right.w &&
    left.h === right.h &&
    (left.rotation ?? 0) === (right.rotation ?? 0) &&
    left.zOrder === right.zOrder &&
    (left.locked === true) === (right.locked === true) &&
    left.from.objectId === right.from.objectId &&
    left.from.side === right.from.side &&
    left.to.objectId === right.to.objectId &&
    left.to.side === right.to.side &&
    left.stroke === right.stroke &&
    left.strokeWidth === right.strokeWidth &&
    CONNECTOR_PATCH_KEYS.every((key) =>
      isSameConnectorPatchValue(key, leftAppearance[key], rightAppearance[key])
    )
  );
}

/** 競合検知に使う revision。未指定（旧データ・作成直後）は 0 として扱う。 */
export function connectorRevisionOf(revision: number | undefined): number {
  return typeof revision === "number" && Number.isSafeInteger(revision) && revision >= 0
    ? revision
    : 0;
}

/**
 * 次の revision。採番余地が尽きた（安全な整数を超える）場合は null を返し、呼び出し側が
 * 保存せずに拒否する（進められないまま保存すると、条件付き書き込みによる排他が成立しなくなる）。
 */
export function nextConnectorRevision(revision: number | undefined): number | null {
  const next = connectorRevisionOf(revision) + 1;
  return Number.isSafeInteger(next) ? next : null;
}

/** revision の採番余地が尽きたときの拒否理由。 */
export const CONNECTOR_REVISION_EXHAUSTED_REASON =
  "この接続矢印は更新回数の上限に達しました。接続矢印を作り直してから編集してください。";

/** 項目単位の競合で更新を拒否するときの理由。 */
export const CONNECTOR_UPDATE_CONFLICT_REASON =
  "接続矢印が他の利用者によって変更されました。最新の内容を確認して操作し直してください。";
